import React, { useEffect, useState } from 'react';
import { adminApi, EmailQueueStats, PendingEmailTask, FailedEmailTask, EmailLog, EmailLogStats } from '../utils/api';

export default function EmailQueue() {
  // Redis-based stats (current queue state)
  const [stats, setStats] = useState<EmailQueueStats | null>(null);
  const [pendingTasks, setPendingTasks] = useState<PendingEmailTask[]>([]);
  const [failedTasks, setFailedTasks] = useState<FailedEmailTask[]>([]);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [failedCount, setFailedCount] = useState<number>(0);

  // Database-backed history
  const [emailHistory, setEmailHistory] = useState<EmailLog[]>([]);
  const [historyTotal, setHistoryTotal] = useState<number>(0);
  const [historyPage, setHistoryPage] = useState<number>(1);
  const [historyPageSize] = useState<number>(50);
  const [emailLogStats, setEmailLogStats] = useState<EmailLogStats | null>(null);

  // Filters and search
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [emailTypeFilter, setEmailTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [daysFilter, setDaysFilter] = useState<number>(7);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'pending' | 'failed' | 'history'>('overview');
  const [retrying, setRetrying] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getEmailQueueStats();
      setStats(response.data);
    } catch (err) {
      setError('Failed to fetch email queue stats');
    }
  };

  const fetchPending = async () => {
    try {
      const response = await adminApi.getPendingEmails();
      setPendingTasks(response.data.tasks || []);
      setPendingCount(response.data.count || 0);
    } catch (err) {
      setError('Failed to fetch pending emails');
    }
  };

  const fetchFailed = async () => {
    try {
      const response = await adminApi.getFailedEmails();
      setFailedTasks(response.data.failed_tasks || []);
      setFailedCount(response.data.count || 0);
    } catch (err) {
      setError('Failed to fetch failed emails');
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await adminApi.getEmailLogHistory({
        page: historyPage,
        page_size: historyPageSize,
        search: searchTerm || undefined,
        email_type: emailTypeFilter || undefined,
        status: statusFilter || undefined,
        days: daysFilter,
      });
      setEmailHistory(response.data.logs);
      setHistoryTotal(response.data.total);
    } catch (err) {
      setError('Failed to fetch email history');
    }
  };

  const fetchEmailLogStats = async () => {
    try {
      const response = await adminApi.getEmailLogStats(daysFilter);
      setEmailLogStats(response.data);
    } catch (err) {
      console.error('Failed to fetch email log stats:', err);
    }
  };

  const retryTask = async (taskId: string) => {
    setRetrying(taskId);
    try {
      await adminApi.retryEmailTask(taskId);
      alert('Task requeued successfully');
      await fetchFailed();
    } catch (err) {
      alert('Failed to retry task');
    } finally {
      setRetrying(null);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchPending(), fetchFailed(), fetchEmailLogStats()]);
      setLoading(false);
    };
    loadData();

    // Auto-refresh every 10 seconds for real-time data
    const interval = setInterval(() => {
      fetchStats();
      fetchEmailLogStats();
      if (activeTab === 'pending') fetchPending();
      if (activeTab === 'failed') fetchFailed();
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyPage, searchTerm, emailTypeFilter, statusFilter, daysFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setHistoryPage(1); // Reset to first page on new search
    fetchHistory();
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  const totalPages = Math.ceil(historyTotal / historyPageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Email Queue & History</h1>
        <button
          onClick={() => {
            fetchStats();
            fetchPending();
            fetchFailed();
            fetchEmailLogStats();
            if (activeTab === 'history') fetchHistory();
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Stats Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Redis Queue Stats */}
        {stats && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Current Queue (Redis)</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-semibold text-blue-600">{stats.pending_in_queue}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Successful</p>
                <p className="text-2xl font-semibold text-green-600">{stats.successful}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Failed</p>
                <p className="text-2xl font-semibold text-red-600">{stats.failed}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Tracked</p>
                <p className="text-2xl font-semibold text-gray-600">{stats.total_tracked}</p>
              </div>
            </div>
          </div>
        )}

        {/* Database History Stats */}
        {emailLogStats && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">History (Last {daysFilter} days)</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Total Emails</p>
                <p className="text-2xl font-semibold text-gray-600">{emailLogStats.total_emails}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Sent</p>
                <p className="text-2xl font-semibold text-green-600">{emailLogStats.sent}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Failed</p>
                <p className="text-2xl font-semibold text-red-600">{emailLogStats.failed}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Queued</p>
                <p className="text-2xl font-semibold text-yellow-600">{emailLogStats.queued}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Pending ({pendingCount})
            </button>
            <button
              onClick={() => setActiveTab('failed')}
              className={`${
                activeTab === 'failed'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Failed ({failedCount})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              History ({historyTotal})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && stats && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Recent Failures</h2>
              {stats.failed_tasks.length === 0 ? (
                <p className="text-gray-500">No recent failures</p>
              ) : (
                <div className="space-y-4">
                  {stats.failed_tasks.map((task) => (
                    <div key={task.task_id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-mono text-sm text-gray-600">Task ID: {task.task_id}</p>
                          <p className="mt-1 text-sm text-red-800">
                            <strong>Error:</strong> {JSON.stringify(task.result)}
                          </p>
                          {task.date_done && (
                            <p className="mt-1 text-xs text-gray-500">Failed: {new Date(task.date_done).toLocaleString()}</p>
                          )}
                        </div>
                        <button
                          onClick={() => retryTask(task.task_id)}
                          disabled={retrying === task.task_id}
                          className="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          {retrying === task.task_id ? 'Retrying...' : 'Retry'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'pending' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Pending Tasks</h2>
              {pendingTasks.length === 0 ? (
                <p className="text-gray-500">No pending tasks</p>
              ) : (
                <div className="space-y-4">
                  {pendingTasks.map((task) => (
                    <div key={task.task_id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="font-mono text-sm text-gray-600">Task ID: {task.task_id}</p>
                      <p className="mt-1 text-sm"><strong>Task:</strong> {task.task_name}</p>
                      <p className="mt-1 text-sm"><strong>Retries:</strong> {task.retries}</p>
                      {task.kwargs && Object.keys(task.kwargs).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-sm font-medium cursor-pointer">Arguments</summary>
                          <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto">
                            {JSON.stringify(task.kwargs, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'failed' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Failed Tasks</h2>
              {failedTasks.length === 0 ? (
                <p className="text-gray-500">No failed tasks</p>
              ) : (
                <div className="space-y-4">
                  {failedTasks.map((task) => (
                    <div key={task.task_id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-mono text-sm text-gray-600">Task ID: {task.task_id}</p>
                          <p className="mt-1 text-sm"><strong>Task:</strong> {task.task_name}</p>
                          <p className="mt-1 text-sm text-red-800">
                            <strong>Error:</strong> {JSON.stringify(task.error)}
                          </p>
                          {task.date_done && (
                            <p className="mt-1 text-xs text-gray-500">Failed: {new Date(task.date_done).toLocaleString()}</p>
                          )}
                          {task.traceback && (
                            <details className="mt-2">
                              <summary className="text-sm font-medium cursor-pointer">Traceback</summary>
                              <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto max-h-64">
                                {task.traceback}
                              </pre>
                            </details>
                          )}
                          {task.kwargs && (
                            <details className="mt-2">
                              <summary className="text-sm font-medium cursor-pointer">Arguments</summary>
                              <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto">
                                {JSON.stringify(task.kwargs, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                        <button
                          onClick={() => retryTask(task.task_id)}
                          disabled={retrying === task.task_id}
                          className="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          {retrying === task.task_id ? 'Retrying...' : 'Retry'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {/* Search and Filters */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <form onSubmit={handleSearch} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Search
                      </label>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Email or subject..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Type
                      </label>
                      <select
                        value={emailTypeFilter}
                        onChange={(e) => {
                          setEmailTypeFilter(e.target.value);
                          setHistoryPage(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">All Types</option>
                        <option value="verification">Verification</option>
                        <option value="password_reset">Password Reset</option>
                        <option value="vpn_request">VPN Request</option>
                        <option value="vpn_approved">VPN Approved</option>
                        <option value="vpn_rejected">VPN Rejected</option>
                        <option value="vpn_connected">VPN Connected</option>
                        <option value="vpn_disconnected">VPN Disconnected</option>
                        <option value="generic">Generic</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setHistoryPage(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">All Status</option>
                        <option value="queued">Queued</option>
                        <option value="sending">Sending</option>
                        <option value="sent">Sent</option>
                        <option value="failed">Failed</option>
                        <option value="retrying">Retrying</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Days
                      </label>
                      <select
                        value={daysFilter}
                        onChange={(e) => {
                          setDaysFilter(Number(e.target.value));
                          setHistoryPage(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="1">Last 24 hours</option>
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Apply Filters
                  </button>
                </form>
              </div>

              {/* Email History Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {emailHistory.map((email) => (
                      <tr key={email.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(email.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {email.email_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {email.recipient_email}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {email.subject}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            email.status === 'sent' ? 'bg-green-100 text-green-800' :
                            email.status === 'failed' ? 'bg-red-100 text-red-800' :
                            email.status === 'queued' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {email.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => setSelectedEmail(email)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-700">
                    Showing {(historyPage - 1) * historyPageSize + 1} to {Math.min(historyPage * historyPageSize, historyTotal)} of {historyTotal} emails
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                      disabled={historyPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1">
                      Page {historyPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))}
                      disabled={historyPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Email Details Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold">Email Details</h2>
                <button
                  onClick={() => setSelectedEmail(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-700">Basic Info</h3>
                  <dl className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm text-gray-500">Type</dt>
                      <dd className="text-sm font-medium">{selectedEmail.email_type}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Status</dt>
                      <dd className="text-sm font-medium">{selectedEmail.status}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Recipient</dt>
                      <dd className="text-sm font-medium">{selectedEmail.recipient_email}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Task ID</dt>
                      <dd className="text-sm font-mono">{selectedEmail.task_id || 'N/A'}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-700">Subject</h3>
                  <p className="mt-1 text-sm">{selectedEmail.subject}</p>
                </div>

                {selectedEmail.error_message && (
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <h3 className="font-semibold text-red-700">Error</h3>
                    <p className="mt-1 text-sm text-red-600">{selectedEmail.error_message}</p>
                  </div>
                )}

                {selectedEmail.html_content && (
                  <div>
                    <h3 className="font-semibold text-gray-700">HTML Preview</h3>
                    <div className="mt-2 border border-gray-200 rounded p-4 bg-gray-50 max-h-96 overflow-y-auto">
                      <iframe
                        srcDoc={selectedEmail.html_content}
                        className="w-full h-96 border-0"
                        title="Email Preview"
                      />
                    </div>
                  </div>
                )}

                {selectedEmail.extra_data && Object.keys(selectedEmail.extra_data).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700">Extra Data</h3>
                    <pre className="mt-2 text-xs bg-gray-50 p-4 rounded overflow-auto max-h-64">
                      {JSON.stringify(selectedEmail.extra_data, null, 2)}
                    </pre>
                  </div>
                )}

                <div>
                  <h3 className="font-semibold text-gray-700">Timestamps</h3>
                  <dl className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm text-gray-500">Created</dt>
                      <dd className="text-sm">{new Date(selectedEmail.created_at).toLocaleString()}</dd>
                    </div>
                    {selectedEmail.queued_at && (
                      <div>
                        <dt className="text-sm text-gray-500">Queued</dt>
                        <dd className="text-sm">{new Date(selectedEmail.queued_at).toLocaleString()}</dd>
                      </div>
                    )}
                    {selectedEmail.sent_at && (
                      <div>
                        <dt className="text-sm text-gray-500">Sent</dt>
                        <dd className="text-sm">{new Date(selectedEmail.sent_at).toLocaleString()}</dd>
                      </div>
                    )}
                    {selectedEmail.failed_at && (
                      <div>
                        <dt className="text-sm text-gray-500">Failed</dt>
                        <dd className="text-sm">{new Date(selectedEmail.failed_at).toLocaleString()}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedEmail(null)}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
